/**
 * A 2D vector class for geometric operations
 *
 * Provides methods for vector arithmetic, magnitude calculations,
 * angle operations, and utility functions for game physics.
 */
export default class Vector {
  /**
   * Creates a new Vector instance
   * @param x - The x component (defaults to 0)
   * @param y - The y component (defaults to 0)
   */
  constructor(
    public x = 0,
    public y = 0,
  ) {}

  /**
   * Adds a scalar value to both x and y components
   * @param scalar - The value to add to both components
   */
  addScalar(scalar: number): void {
    this.x += scalar;
    this.y += scalar;
  }

  /**
   * Multiplies both x and y components by a scalar value
   * @param multiplier - The value to multiply both components by
   */
  multiply(multiplier: number): void {
    this.x *= multiplier;
    this.y *= multiplier;
  }

  /**
   * Creates a copy of this vector
   * @returns A new Vector instance with the same x and y values
   */
  clone(): Vector {
    return new Vector(this.x, this.y);
  }

  /**
   * Calculates the squared magnitude of the vector
   * @returns The squared Euclidean distance (avoids expensive sqrt)
   */
  lengthSq(): number {
    return this.x * this.x + this.y * this.y;
  }

  /**
   * Calculates the magnitude (length) of the vector
   * @returns The Euclidean distance from the origin
   */
  length(): number {
    return Math.sqrt(this.lengthSq());
  }

  /**
   * Calculates the angle of the vector in radians
   * @returns The angle in radians, adjusted by -90 degrees (π/2)
   */
  angle(): number {
    return Math.atan2(this.y, this.x) - Math.PI / 2;
  }

  /**
   * Clamps both x and y components to a maximum value
   * @param max - The maximum value for both components
   */
  clampMax(max: number): void {
    if (this.x > max) this.x = max;
    if (this.y > max) this.y = max;
  }

  /**
   * Sets the x and y components
   * @param x - The x component
   * @param y - The y component
   */
  set(x: number, y: number): void {
    this.x = x;
    this.y = y;
  }

  /**
   * Sets both x and y components to the same value
   * @param value - The value to set for both components
   */
  both(value: number): void {
    this.x = value;
    this.y = value;
  }

  /**
   * Creates a zero vector (0, 0)
   * @returns A new Vector instance at the origin
   */
  static zero(): Vector {
    return new Vector(0, 0);
  }
}
